import React, { useState, useEffect } from 'react';
import { FaShare, FaCopy, FaGlobe, FaLock, FaExclamationTriangle, FaTimes } from 'react-icons/fa';

function ShareModal({ idea, onClose, onUpdateIdea }) {
  const [isPublic, setIsPublic] = useState(idea?.is_public || false);
  const [copied, setCopied] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  const shareUrl = `${window.location.origin}/idea/${idea?.id}`;

  useEffect(() => {
    setIsPublic(idea?.is_public || false);
  }, [idea]);

  const handleTogglePublic = async () => {
    try {
      setIsUpdating(true);
      setError('');
      
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('You need to be logged in to change visibility');
        return;
      }
      
      const updatedIdea = {
        ...idea,
        is_public: !isPublic
      };
      
      // Call the update function passed from parent
      await onUpdateIdea(updatedIdea);
      setIsPublic(!isPublic);
    } catch (err) {
      console.error('Failed to update idea visibility:', err);
      setError('Failed to update idea visibility. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[var(--primary)]/10 rounded-full flex items-center justify-center">
                <FaShare className="text-[var(--primary)] text-xl" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[var(--text-primary)]">Share Idea</h3>
                <p className="text-sm text-[var(--text-secondary)]">"{idea?.title}"</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              <FaTimes />
            </button>
          </div>

          {/* Visibility Toggle */}
          <div className="mb-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
            
            <div className="flex items-center justify-between p-4 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)]">
              <div className="flex items-center gap-3">
                {isPublic ? (
                  <FaGlobe className="text-success text-xl" />
                ) : (
                  <FaLock className="text-[var(--text-secondary)] text-xl" />
                )}
                <div>
                  <h4 className="font-medium text-[var(--text-primary)]">
                    {isPublic ? 'Public' : 'Private'}
                  </h4>
                  <p className="text-sm text-[var(--text-secondary)]">
                    {isPublic 
                      ? 'Anyone with the link can view this idea'
                      : 'Only you and collaborators can view this idea'
                    }
                  </p>
                </div>
              </div>
              <button
                onClick={handleTogglePublic}
                disabled={isUpdating}
                className={`relative inline-flex h-6 w-11 items-center rounded-full border border-[var(--text-primary)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 ${
                  isPublic ? 'bg-[var(--primary)]' : 'bg-[var(--bg-primary)]'
                } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-[var(--text-primary)] transition-transform ${
                    isPublic ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Warning for private ideas */}
            {!isPublic && (
              <div className="mt-3 p-3 bg-[var(--primary)]/10 border border-[var(--primary)]/20 rounded-lg">
                <div className="flex items-center gap-2 text-[var(--primary)]">
                  <FaExclamationTriangle />
                  <span className="text-sm font-medium">Private Idea</span>
                </div>
                <p className="text-sm text-[var(--text-secondary)] mt-1">
                  Your idea must be set to public to be shareable with others outside your collaboration.
                </p>
              </div>
            )}
          </div>

          {/* Share Link */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              Share Link
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="flex-1 p-3 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] text-sm"
              />
              <button
                onClick={handleCopyLink}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 min-w-[100px] justify-center ${
                  copied 
                    ? 'bg-success text-white' 
                    : 'bg-[var(--primary)] text-white hover:opacity-90'
                }`}
              >
                <FaCopy />
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-color)] hover:bg-[var(--bg-hover)] transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShareModal;
